import React, { useEffect, useState, useRef, Component } from 'react';
/**
 * MoneyAssembler — Interactive coin/bill selection for money questions.
 *
 * DDD co-locatie: moved from components/practice/ in s65.
 * Optimized: style definitions extracted to moneyStyles.ts (~140r).
 */
import { motion, AnimatePresence } from 'framer-motion';
import type { Question, MoneyDenom } from '../../content/types';
import { X, Wallet, RotateCcw } from 'lucide-react';
import { error as logError } from '../../utils/logger';
import { getMoneyStyles, isBill, getDenominationDisplay } from '../../visuals/components/moneyStyles';
import { formatMoneyDisplay } from '../../utils/moneyFormatters';
// ── Types ────────────────────────────────────────────────────
interface MoneyAssemblerProps {
  question: Question;
  onComplete: (userAnswer: string) => void;
  onReadyChange?: (isReady: boolean) => void;
  shouldValidate?: boolean;
}
// ── MoneyItem Sub-component ──────────────────────────────────
function MoneyItem({
  cents,
  allowedDenoms,
  onClick,
  disabled = false,
  small = false,
  isFocused = false
}: {
  cents: number;
  allowedDenoms: MoneyDenom[];
  onClick?: () => void;
  disabled?: boolean;
  small?: boolean;
  isFocused?: boolean;
}) {
  const styles = getMoneyStyles(cents);
  const isBillItem = isBill(cents, allowedDenoms);
  const scaleClass = small ? 'scale-75 origin-center' : '';
  return <motion.button whileHover={!disabled && !small ? {
    scale: 1.08,
    rotate: isBillItem ? 0 : 2,
    y: -3
  } : {}} whileTap={!disabled && !small ? {
    scale: 0.92
  } : {}} onClick={onClick} disabled={disabled} className={`
        relative flex items-center justify-center
        ${styles.container} ${styles.bg} ${styles.border}
        ${scaleClass}
        transition-all duration-200
        ${!disabled && !small ? 'hover:shadow-2xl cursor-pointer' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${isFocused ? 'ring-4 ring-indigo-400 ring-offset-2' : ''}
      `} aria-label={getDenominationDisplay(cents, allowedDenoms)} data-id="element-11532" data-mp="3n7ig1ujwvhx1">
      
      <div className={`${styles.inner} relative z-10`} data-id="element-11533" data-mp="1zbu5ks5wpyoe">
        <span className={`${styles.text} drop-shadow-md`} data-id="element-11534" data-mp="23a9ookyvamwj">
          {getDenominationDisplay(cents, allowedDenoms)}
          {cents < 100 && <span className="text-[0.65em] ml-0.5" data-id="element-11535" data-mp="1pjo6kyh8mgzp">c</span>}
        </span>
      </div>
      {isBillItem && <div className="absolute right-3 top-0 bottom-0 w-2 bg-white/15 border-l border-r border-white/25 rounded-r-lg" data-id="element-11536" data-mp="1uh6huviq98av" />}
    </motion.button>;
}
// ── Main Component ───────────────────────────────────────────
export function MoneyAssembler({
  question,
  onComplete,
  onReadyChange,
  shouldValidate = false
}: MoneyAssemblerProps) {
  // CANONICAL: Read ONLY from data.money (no legacy fallback)
  const moneyConfig = question.data?.money;
  const allowedDenoms: MoneyDenom[] = moneyConfig?.allowedDenoms || [];
  const availableDenominations = allowedDenoms.map(d => d.valueCents);
  const [selectedCoins, setSelectedCoins] = useState<Array<{
    id: string;
    value: number;
  }>>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const prevValidateRef = useRef(false);
  const isReady = selectedCoins.length > 0;
  const handleRemoveCoin = (index: number) => {
    if (shouldValidate) return;
    setSelectedCoins(prev => prev.filter((_, i) => i !== index));
  };
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  // Rising-edge detection for shouldValidate (consistent with all other renderers)
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const countMap = new Map<number, number>();
      selectedCoins.forEach(coinObj => {
        countMap.set(coinObj.value, (countMap.get(coinObj.value) || 0) + 1);
      });
      const tokens = Array.from(countMap.entries()).filter(([_, count]) => count > 0).map(([valueCents, count]) => ({
        valueCents,
        count
      }));
      onComplete(JSON.stringify(tokens));
    }
  }, [shouldValidate, selectedCoins, onComplete]);
  // Reset when question changes
  useEffect(() => {
    setSelectedCoins([]);
    setFocusedIndex(0);
    prevValidateRef.current = false;
  }, [question.id]);
  // Keyboard support
  // INTERACTIVE-2.2: scope the window-level listener so it does NOT swallow
  // keystrokes intended for a focused form control elsewhere on the page.
  // We bail if the active element is a text/number input or a contenteditable
  // surface — those have their own Backspace/Space/Enter semantics.
  useEffect(() => {
    const isTypingTarget = (el: Element | null): boolean => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT') {
        const inputType = (el as HTMLInputElement).type;
        // Non-text inputs (button, checkbox, radio) don't conflict
        return !['button', 'checkbox', 'radio', 'submit', 'reset'].includes(inputType);
      }
      if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if ((el as HTMLElement).isContentEditable) return true;
      return false;
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(document.activeElement)) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusedIndex(prev => Math.min(availableDenominations.length - 1, prev + 1));
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setSelectedCoins(prev => [...prev, availableDenominations[focusedIndex]]);
      } else if (e.key === 'Backspace' && selectedCoins.length > 0) {
        e.preventDefault();
        setSelectedCoins(prev => prev.slice(0, -1));
      } else if (e.key === 'Escape' && selectedCoins.length > 0) {
        e.preventDefault();
        setSelectedCoins([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [availableDenominations, focusedIndex, selectedCoins]);
  // Guard: missing config — rendered AFTER all hooks
  if (!moneyConfig || !moneyConfig.allowedDenoms) {
    logError('MoneyAssembler: Missing money config in question.data.money');
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-11537" data-mp="d9gpe8ora559">
        <p className="text-red-700 font-bold" data-id="element-11538" data-mp="dbjaceb1x834">⚠️ Geld configuratie ontbreekt</p>
        <p className="text-sm text-red-600 mt-2" data-id="element-11539" data-mp="3upeyvsnjacgy">
          Deze vraag heeft geen geldige geld data.
        </p>
      </div>;
  }
  return <div className="w-full max-w-3xl mx-auto space-y-6" data-id="element-11540" data-mp="r97ypw4buxcx">
      {/* Keyboard hints - only on desktop */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-center hidden md:block" data-id="element-11541" data-mp="vzy3b74xiw6k">
        <p className="text-xs text-indigo-700 font-medium" data-id="element-11542" data-mp="2gq3jnskq3f56">
          💡 <strong data-id="element-11543" data-mp="2goikfpngr80z">Toetsenbord:</strong> ← → om te navigeren • Enter/Spatie om
          te selecteren • Backspace om laatste te verwijderen • Esc om te
          resetten
        </p>
      </div>

      {/* Step 1: Selection */}
      <div className="space-y-3" data-id="element-11544" data-mp="2lh1z6pxw097d">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-11545" data-mp="2li4qgyz4gdnt">
          <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-11546" data-mp="1xsaeskqjgv55">
            1
          </span>
          Kies munten en biljetten
        </h3>

        <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm" data-id="element-11547" data-mp="26sytlhnrazj3">
          <div className="flex flex-wrap gap-3 md:gap-4 justify-center items-center" data-id="element-11548" data-mp="21s1bf52qy5hl">
            {availableDenominations.map((denomination: number, index: number) => <motion.div key={`denom-${denomination}`} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.03
          }} data-id="element-11549" data-mp="3d89jtn4v0b16" data-mpi={index}>
                
                  <MoneyItem cents={denomination} allowedDenoms={allowedDenoms} onClick={() => setSelectedCoins(prev => [...prev, {
              id: crypto.randomUUID(),
              value: denomination
            }])} isFocused={index === focusedIndex} data-id="element-11550" data-mp="64b00kggs7yk" />
                
                </motion.div>)}
          </div>
        </div>
      </div>

      {/* Step 2: Wallet */}
      <div className="space-y-3" data-id="element-11551" data-mp="othneks7dnqp">
        <div className="flex items-center justify-between" data-id="element-11552" data-mp="ospszdacdw20">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2" data-id="element-11553" data-mp="tuousiqfokwu">
            <span className="w-5 h-5 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px]" data-id="element-11554" data-mp="xtxojnlh4ise">
              2
            </span>
            Jouw portemonnee
          </h3>

          {selectedCoins.length > 0 && <motion.button initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} onClick={() => setSelectedCoins([])} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors" data-id="element-11555" data-mp="2zooikj73h3dy">
            
              <RotateCcw className="w-3.5 h-3.5" data-id="element-11556" data-mp="2ed94q14ssm5a" />
              Opnieuw
            </motion.button>}
        </div>

        <div className="relative min-h-[140px] bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden" data-id="element-11557" data-mp="3b15mvafvvpim">
          {selectedCoins.length === 0 && <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 pointer-events-none" data-id="element-11558" data-mp="320h8ufqhpp5q">
              <Wallet className="w-10 h-10 mb-2 opacity-30" data-id="element-11559" data-mp="2x1ntsivk1q3g" />
              <p className="text-sm font-medium" data-id="element-11560" data-mp="1qmmxgc2mb5wc">De portemonnee is nog leeg</p>
            </div>}

          <div className="relative z-10 p-4 flex flex-wrap content-start gap-2" data-id="element-11561" data-mp="1lmhd7gyogloa">
            <AnimatePresence data-id="element-11562" data-mp="17bdwyq5nxpwc">
              {selectedCoins.map((coinObj, index) => <motion.button key={coinObj.id} layout initial={{
              opacity: 0,
              scale: 0.5,
              y: -20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.5,
              y: 20
            }} onClick={() => handleRemoveCoin(index)} disabled={shouldValidate} className="relative group touch-manipulation" aria-label={`Verwijder ${formatMoneyDisplay(coinObj.value)}`} data-id="element-11563" data-mp="17bm8oeq1up5y" data-mpi={index}>
                
                  <MoneyItem cents={coinObj.value} allowedDenoms={allowedDenoms} small disabled data-id="element-11564" data-mp="1z2a7hk493igg" />
                
                  {!shouldValidate && <span aria-hidden="true" className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-11565" data-mp="1v29vs78jdld3">
                  
                      <X className="w-3 h-3" data-id="element-11566" data-mp="3mon0ygntddoo" />
                    </span>}
                </motion.button>)}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>;
}